
<div class="space-y-6">
    <!-- Header with Score -->
    <div class="bg-slate-900 border border-slate-800 rounded-xl p-6 flex flex-col md:flex-row justify-between items-center gap-6">
        <div>
            <h2 class="text-2xl font-bold text-white flex items-center gap-3">
                <i class="fa-solid fa-user-doctor text-purple-500"></i> System Diagnostics Center
            </h2>
            <p class="text-slate-400 mt-1">Real-time health monitoring and automated audits.</p>
            <p class="text-xs text-slate-500 mt-2">Last Scan: {{ lastScan() ? (lastScan() | date:'medium') : 'Never' }}</p>
        </div>
        <div class="flex items-center gap-6">
            <div class="text-center">
                <div class="text-4xl font-black" [class]="scoreColor()">{{ healthScore() }}</div>
                <div class="text-xs text-slate-500 uppercase tracking-widest">Health Grade</div>
            </div>
            <button (click)="runScan('all')" [disabled]="isLoading()" class="bg-purple-600 hover:bg-purple-500 text-white px-6 py-3 rounded-lg font-bold transition-all shadow-lg flex items-center gap-2 disabled:opacity-50">
                <i class="fa-solid" [class.fa-spin]="isLoading()" [class.fa-rotate-right]="!isLoading()" [class.fa-circle-notch]="isLoading()"></i>
                {{ isLoading() ? 'Scanning...' : 'Run All Scans' }}
            </button>
        </div>
    </div>

    <!-- Tabs -->
    <div class="flex gap-2 border-b border-slate-700 pb-1 overflow-x-auto">
        <button (click)="setTab('health')" [class]="activeTab() === 'health' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-heart-pulse mr-2"></i> System Health
        </button>
        <button (click)="setTab('integrity')" [class]="activeTab() === 'integrity' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-database mr-2"></i> Data Integrity
        </button>
        <button (click)="setTab('security')" [class]="activeTab() === 'security' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-shield-halved mr-2"></i> Security Audit
        </button>
        <button (click)="setTab('alerts')" [class]="activeTab() === 'alerts' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-triangle-exclamation mr-2"></i> Client Errors
        </button>
         <button (click)="setTab('logic')" [class]="activeTab() === 'logic' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-vial mr-2"></i> Logic Verif.
        </button>
        <button (click)="setTab('simulator')" [class]="activeTab() === 'simulator' ? 'text-purple-400 border-b-2 border-purple-400 font-bold' : 'text-slate-400 hover:text-white'" class="px-4 py-2 transition-colors whitespace-nowrap">
            <i class="fa-solid fa-robot mr-2"></i> Webhook Sim.
        </button>
    </div>

    <!-- Content -->
    @switch (activeTab()) {
        @case ('health') {
            <app-smoke-test-widget></app-smoke-test-widget>
        }
        @case ('integrity') {
            <div class="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div class="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                    <h3 class="font-bold text-white">Data Consistency Issues</h3>
                    <span class="bg-slate-800 text-slate-300 px-2 py-1 rounded text-xs">{{ integrityIssues().length }} Issues</span>
                </div>
                @if(integrityIssues().length === 0) {
                     <div class="p-10 text-center text-slate-500">
                        <i class="fa-solid fa-check-circle text-4xl mb-4 text-emerald-500/50"></i>
                        <p>All data integrity checks passed.</p>
                     </div>
                } @else {
                    <div class="divide-y divide-slate-800">
                        @for(issue of integrityIssues(); track $index) {
                            <div class="p-4 flex items-start gap-4">
                                <span class="px-2 py-1 rounded text-xs font-bold border shrink-0 mt-1" [class]="getSeverityClass(issue.severity)">{{ issue.severity }}</span>
                                <div class="flex-grow">
                                    <h4 class="text-white font-bold text-sm">{{ issue.category }}</h4>
                                    <p class="text-slate-400 text-sm mt-1">{{ issue.message }}</p>
                                </div>
                                <div class="text-right shrink-0">
                                    <span class="text-xs text-purple-400 block mb-1">Recommended Action:</span>
                                    <span class="text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded border border-slate-700 font-mono">{{ issue.action }}</span>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        }
        @case ('security') {
            <div class="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div class="p-4 border-b border-slate-800 bg-slate-950/50 flex justify-between items-center">
                    <h3 class="font-bold text-white">Security Vulnerabilities</h3>
                    <span class="bg-slate-800 text-slate-300 px-2 py-1 rounded text-xs">{{ securityRisks().length }} Risks</span>
                </div>
                @if(securityRisks().length === 0) {
                     <div class="p-10 text-center text-slate-500">
                        <i class="fa-solid fa-lock text-4xl mb-4 text-emerald-500/50"></i>
                        <p>No critical security risks found.</p>
                     </div>
                } @else {
                    <div class="divide-y divide-slate-800">
                        @for(risk of securityRisks(); track $index) {
                            <div class="p-4 flex items-start gap-4 hover:bg-red-900/10 transition-colors">
                                <span class="px-2 py-1 rounded text-xs font-bold border shrink-0 mt-1" [class]="getSeverityClass(risk.severity)">{{ risk.severity }}</span>
                                <div class="flex-grow">
                                    <h4 class="text-white font-bold text-sm">{{ risk.category }}</h4>
                                    <p class="text-slate-400 text-sm mt-1">{{ risk.message }}</p>
                                </div>
                                <div class="text-right shrink-0">
                                    <span class="text-xs text-purple-400 block mb-1">Fix Command:</span>
                                    <span class="text-xs text-slate-300 bg-slate-800 px-2 py-1 rounded border border-slate-700 font-mono">{{ risk.action }}</span>
                                </div>
                            </div>
                        }
                    </div>
                }
            </div>
        }
        @case ('alerts') {
             <div class="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
                <div class="p-4 border-b border-slate-800 bg-slate-950/50">
                    <h3 class="font-bold text-white">Recent Client Errors & Alerts</h3>
                </div>
                <div class="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
                    @for(alert of recentAlerts(); track alert.id) {
                         <div class="p-4 hover:bg-slate-800/50 transition-colors">
                            <div class="flex justify-between mb-2">
                                <span class="text-xs text-slate-500">{{ alert.created_at | date:'medium' }}</span>
                                <span class="text-xs font-bold" [class.text-red-400]="alert.severity === 'CRITICAL'" [class.text-yellow-400]="alert.severity === 'WARNING'">{{ alert.severity }}</span>
                            </div>
                            <h4 class="text-white font-bold text-sm mb-1">{{ alert.source }}</h4>
                            <p class="text-slate-400 text-xs font-mono break-all">{{ alert.message }}</p>
                            
                            @if(alert.details?.user_agent) {
                                <div class="mt-2 p-2 bg-black/30 rounded text-[10px] text-slate-500 font-mono">
                                    UA: {{ alert.details.user_agent }}
                                </div>
                            }
                         </div>
                    } @empty {
                        <div class="p-10 text-center text-slate-500">
                             <p>No recent alerts.</p>
                        </div>
                    }
                </div>
             </div>
        }
        @case ('logic') {
            <!-- NEW COMPONENT: Encapsulates all logic verification tests -->
            <app-logic-verification></app-logic-verification>
        }
        @case ('simulator') {
            <div class="max-w-2xl mx-auto space-y-6 animate-fade-in">
                <div class="bg-slate-900 border border-slate-800 rounded-xl p-6">
                    <h3 class="text-xl font-bold text-white mb-2 flex items-center gap-2">
                        <i class="fa-solid fa-money-bill-transfer text-emerald-400"></i> Payment Webhook Simulator
                    </h3>
                    <p class="text-slate-400 text-sm mb-6">Simulates a successful payment signal arriving from a gateway (Omise/SlipOK). Test how the wallet and SaaS logic reacts.</p>
                    
                    <div class="space-y-4">
                        <div>
                            <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Target Store (Clinic)</label>
                            <select [(ngModel)]="simStoreId" class="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-white focus:ring-2 focus:ring-purple-500">
                                <option value="">-- Select a Store to Top-up --</option>
                                @for(s of stores(); track s.id) {
                                    <option [value]="s.id">{{ s.name }} (Wallet: ฿{{ s.wallet_balance }})</option>
                                }
                            </select>
                        </div>

                        <div class="grid grid-cols-2 gap-4">
                            <div>
                                <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Amount (THB)</label>
                                <input type="number" [(ngModel)]="simAmount" class="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-white font-bold">
                            </div>
                            <div>
                                <label class="block text-xs font-bold text-slate-500 uppercase mb-1">Transaction Ref ID</label>
                                <input type="text" [(ngModel)]="simRefId" class="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-white font-mono text-xs">
                            </div>
                        </div>

                        <div class="pt-4">
                            <button (click)="runSimulatePayment()" [disabled]="!simStoreId() || isSimulating()" 
                                    class="w-full py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl font-bold shadow-lg transition-all flex items-center justify-center gap-3 disabled:opacity-50">
                                @if(isSimulating()) { <i class="fa-solid fa-circle-notch fa-spin"></i> Simulating Signal... }
                                @else { <i class="fa-solid fa-bolt"></i> Send Success Signal }
                            </button>
                        </div>
                    </div>
                </div>

                @if(simResult(); as res) {
                    <div class="p-6 rounded-xl border animate-bounce-in"
                         [class.bg-green-900_20]="res.status === 'PASS'"
                         [class.border-green-500_50]="res.status === 'PASS'"
                         [class.bg-yellow-900_20]="res.status === 'REJECTED'"
                         [class.border-yellow-500_50]="res.status === 'REJECTED'"
                         [class.bg-red-900_20]="res.status === 'ERROR'"
                         [class.border-red-500_50]="res.status === 'ERROR'">
                        
                        <div class="flex items-center gap-3 mb-4">
                             <div class="w-10 h-10 rounded-full flex items-center justify-center"
                                  [class.bg-green-500]="res.status === 'PASS'"
                                  [class.bg-yellow-500]="res.status === 'REJECTED'"
                                  [class.bg-red-500]="res.status === 'ERROR'">
                                 <i class="fa-solid" [class]="res.status === 'PASS' ? 'fa-check' : (res.status === 'REJECTED' ? 'fa-shield-halved' : 'fa-xmark')"></i>
                             </div>
                             <div>
                                 <h4 class="font-bold text-white">{{ res.status }}</h4>
                                 <p class="text-sm text-slate-300">{{ res.message }}</p>
                             </div>
                        </div>

                        <div class="bg-slate-950 p-4 rounded-lg font-mono text-xs text-slate-400 overflow-x-auto">
                            <pre>{{ res | json }}</pre>
                        </div>
                    </div>
                }
            </div>
        }
    }
</div>
